'use client'

import { cn } from '../../../lib/cn'

type BeamsPosition = 'center' | 'top' | 'bottom' | 'left' | 'right'

interface BeamsInternalProps {
  className?: string
  position: BeamsPosition
}

/**
 * Internal Beams component - use position-specific exports instead
 */
function BeamsInternal({ className, position }: BeamsInternalProps) {
  const vignettePosition = {
    center: '50% 50%',
    top: '50% 20%',
    bottom: '50% 80%',
    left: '20% 20%',
    right: '80% 20%',
  }[position]

  const spotlightPosition = {
    center: '50% 45%',
    top: '50% 25%',
    bottom: '50% 75%',
    left: '25% 25%',
    right: '75% 25%',
  }[position]

  return (
    <div
      className={cn('pointer-events-none absolute inset-0 z-0 overflow-hidden', className)}
    >
      <style>{`
        .beams-gradient-${position} {
          background: linear-gradient(
            135deg,
            color-mix(in srgb, var(--primary) 4%, transparent) 0%,
            color-mix(in srgb, var(--primary) 8%, transparent) 40%,
            color-mix(in srgb, var(--primary) 10%, transparent) 50%,
            color-mix(in srgb, var(--primary) 8%, transparent) 60%,
            color-mix(in srgb, var(--primary) 4%, transparent) 100%
          );
        }

        .beams-blinds-${position} {
          background: repeating-linear-gradient(
            135deg,
            transparent 0px,
            color-mix(in srgb, var(--background) 25%, transparent) 30px,
            color-mix(in srgb, var(--background) 40%, transparent) 40px
          );
        }

        .beams-spotlight-${position} {
          background: radial-gradient(
            ellipse 60% 45% at ${spotlightPosition},
            color-mix(in srgb, var(--primary) 5%, transparent) 0%,
            transparent 50%
          );
        }

        .beams-vignette-${position} {
          background: radial-gradient(
            ellipse ${position === 'left' || position === 'right' ? '45% 70%' : '60% 50%'} at ${vignettePosition},
            transparent 0%,
            color-mix(in srgb, var(--background) 20%, transparent) 25%,
            color-mix(in srgb, var(--background) 50%, transparent) 45%,
            color-mix(in srgb, var(--background) 80%, transparent) 65%,
            var(--background) 85%
          );
        }

        /* Mobile: always center regardless of position prop */
        @media (max-width: 768px) {
          .beams-gradient-${position} {
            background: linear-gradient(
              135deg,
              color-mix(in srgb, var(--primary) 4%, transparent) 0%,
              color-mix(in srgb, var(--primary) 8%, transparent) 40%,
              color-mix(in srgb, var(--primary) 10%, transparent) 50%,
              color-mix(in srgb, var(--primary) 8%, transparent) 60%,
              color-mix(in srgb, var(--primary) 4%, transparent) 100%
            );
          }

          .beams-spotlight-${position} {
            background: radial-gradient(
              ellipse 60% 45% at 50% 45%,
              color-mix(in srgb, var(--primary) 5%, transparent) 0%,
              transparent 50%
            );
          }

          .beams-vignette-${position} {
            background: radial-gradient(
              ellipse 60% 50% at 50% 50%,
              transparent 0%,
              color-mix(in srgb, var(--background) 20%, transparent) 25%,
              color-mix(in srgb, var(--background) 50%, transparent) 45%,
              color-mix(in srgb, var(--background) 80%, transparent) 65%,
              var(--background) 85%
            );
          }
        }
      `}</style>

      {/* Base gradient showing through blinds */}
      <div className={`beams-gradient-${position} absolute inset-0`} />

      {/* Center spotlight for depth */}
      <div className={`beams-spotlight-${position} absolute inset-0`} />

      {/* Diagonal blinds with gradient shading (sawtooth pattern) */}
      <div className={`beams-blinds-${position} absolute -inset-[50%] h-[200%] w-[200%]`} />

      {/* Vignette to fade edges */}
      <div className={`beams-vignette-${position} absolute inset-0`} />
    </div>
  )
}

export interface BeamsProps {
  className?: string
}

/**
 * Beams - Diagonal gradient blinds background effect (centered)
 *
 * Creates diagonal blinds with gradient shading within each blind,
 * giving a 3D venetian blind appearance.
 *
 * Fixed settings - use position-specific variants for different placements:
 * - BeamsCenter (default)
 * - BeamsTop
 * - BeamsBottom
 * - BeamsLeft
 * - BeamsRight
 *
 * @example
 * ```tsx
 * <Beams />
 * <BeamsTop />
 * ```
 */
export function Beams({ className }: BeamsProps) {
  return <BeamsInternal position="center" className={className} />
}

export function BeamsCenter({ className }: BeamsProps) {
  return <BeamsInternal position="center" className={className} />
}

export function BeamsTop({ className }: BeamsProps) {
  return <BeamsInternal position="top" className={className} />
}

export function BeamsBottom({ className }: BeamsProps) {
  return <BeamsInternal position="bottom" className={className} />
}

export function BeamsLeft({ className }: BeamsProps) {
  return <BeamsInternal position="left" className={className} />
}

export function BeamsRight({ className }: BeamsProps) {
  return <BeamsInternal position="right" className={className} />
}
